/**
 * Pre-Launch Config TTL Cache
 *
 * Module-level singleton cache for the pre-launch config API response.
 * Keyed by marketSlug so different markets don't share entries.
 *
 * - 5-minute TTL (AC-02)
 * - Stale-while-revalidate: returns stale immediately, refreshes in background (AC-04)
 * - Errors clear the cache entry (AC-06)
 * - In-memory only — resets on hard refresh (AC-05)
 *
 * SCRUM-1462 / Relates-to: SCRUM-1439
 */

const TTL_MS = 5 * 60 * 1000; // 5 minutes

// Module-level singleton, keyed by marketSlug.
// { [slug]: { data, fetchedAt } }
let _cache = {};

/**
 * Get the pre-launch config for a given market, using the TTL cache.
 *
 * @param {string} marketSlug  - The market slug used as cache key and passed to fetchFn
 * @param {Function} fetchFn   - Async function that fetches fresh config; receives marketSlug
 * @returns {Promise<*>} The config data (from cache or fresh fetch)
 */
export async function getPreLaunchConfig(marketSlug, fetchFn) {
  const now = Date.now();
  const entry = _cache[marketSlug];

  // AC-02: Cache hit within TTL — return immediately
  if (entry && (now - entry.fetchedAt) < TTL_MS) {
    return entry.data;
  }

  // AC-04: Stale-while-revalidate — return stale immediately, refresh in background
  if (entry && (now - entry.fetchedAt) >= TTL_MS) {
    const stale = entry.data;
    fetchFn(marketSlug).then(fresh => {
      _cache[marketSlug] = { data: fresh, fetchedAt: Date.now() };
    }).catch(() => {
      // AC-06: Error clears the stale entry
      delete _cache[marketSlug];
    });
    return stale;
  }

  // AC-03: Cache miss — fetch fresh, populate cache
  try {
    const data = await fetchFn(marketSlug);
    _cache[marketSlug] = { data, fetchedAt: Date.now() };
    return data;
  } catch (err) {
    // AC-06: Do not cache failures
    delete _cache[marketSlug];
    throw err;
  }
}

/**
 * Synchronous read of cached pre-launch enabled state.
 * Returns true only if the cache contains a valid entry indicating enabled.
 * Does NOT fetch — call getPreLaunchConfig first to populate.
 */
export function getCachedPreLaunchEnabled(marketSlug = 'default') {
  const entry = _cache[marketSlug];
  if (!entry) { return false; }
  return !!(entry.data?.success && entry.data?.data?.enabled);
}

/**
 * Reset the entire cache. Exported for testing only.
 * Simulates a hard refresh (module reload resets module state).
 */
export function _resetCacheForTesting() {
  _cache = {};
}
